import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import moment from "moment";
import { M, RowKV } from "./managementUi";
import { formatVnd } from "../utils/financeFormat";

// =============================================================
// Phần dùng chung của "Thu nhập chờ chi trả" (cá nhân + quản lý).
// Khớp mobile-app-v2:
//   src/modules/finance/income/components/IncomeUnpaidListCard.tsx
//   khối "CHƯA CHI TRẢ THEO DỰ ÁN" trong unpaid/list.tsx & unpaid/locking-list.tsx
// =============================================================

// Khớp PaymentStatusEnum của BE (TotalCommission.paymentStatus).
export const PAYMENT_STATUS = {
  UNPAID: "UNPAID",
  PARTIAL: "PARTIAL",
};

// "Tất cả" = không lọc; còn lại khớp PaymentStatusEnum ở BE.
export const UNPAID_STATUS_OPTIONS = [
  { key: "all", label: "Tất cả" },
  { key: PAYMENT_STATUS.UNPAID, label: "Chờ thanh toán" },
  { key: PAYMENT_STATUS.PARTIAL, label: "Đang thanh toán" },
];

// Nhãn + màu badge theo trạng thái thanh toán của căn.
const STATUS_UI = {
  [PAYMENT_STATUS.UNPAID]: { label: "CHỜ THANH TOÁN", bg: "#FFF7E8", color: "#F79D0D" },
  [PAYMENT_STATUS.PARTIAL]: { label: "ĐANG THANH TOÁN", bg: "#EFF6FF", color: "#2F80ED" },
};

export const unpaidStatusUi = (status) =>
  STATUS_UI[status] || STATUS_UI[PAYMENT_STATUS.UNPAID];

// Tạm ẩn khối "Vì sao chưa được chi trả?" theo yêu cầu (khớp app). BE vẫn trả
// `unpaidReasons` nên chỉ cần đổi cờ này về true là hiện lại.
const SHOW_UNPAID_REASONS = false;

export const unpaidTitleOf = (item) => {
  const projectName = item?.project?.name || item?.projectObject?.name || "";
  const apartmentCode = item?.apartment?.code || item?.apartmentObject?.code || "";
  return `${projectName} ${projectName && apartmentCode ? "-" : ""} ${apartmentCode}`.trim();
};

export const unpaidCustomerOf = (item) =>
  item?.customer?.name || item?.customerObject?.name || "-";

export const unpaidDateOf = (item) =>
  item?.bookingTime ? moment(item.bookingTime).format("DD/MM/YYYY") : "--/--/----";

/* ------------------------------------------------- thẻ giao dịch (mobile) */
export const UnpaidLockingCard = ({ item, onClick }) => {
  const ui = unpaidStatusUi(item?.paymentStatus);
  const reasons = SHOW_UNPAID_REASONS ? item?.unpaidReasons || [] : [];

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      onClick={onClick}
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
    >
      <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
        {unpaidTitleOf(item) || "-"}
      </Text>

      <Flex align="center" gap="8px" mt="8px" flexWrap="wrap">
        <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
          {unpaidDateOf(item)}
        </Text>
        <Box bg={ui.bg} borderRadius="6px" px="8px" py="3px">
          <Text fontSize="11px" fontWeight={600} color={ui.color}>
            {ui.label}
          </Text>
        </Box>
      </Flex>

      <Box h="1px" bg={M.border} mt="12px" mb="12px" />

      <Flex direction="column" gap="8px">
        <RowKV label="Khách hàng" value={unpaidCustomerOf(item)} />
        <RowKV label="PDV của tôi" value={formatVnd(item?.myRemainingRevenueAfterTax || 0)} />
        <RowKV
          label="Đã được chi trả"
          value={formatVnd(item?.myPaidRemainingRevenueAfterTax || 0)}
        />
        <RowKV
          label="Chưa được chi trả"
          value={formatVnd(item?.myUnpaidRemainingRevenueAfterTax || 0)}
          valueColor={M.primary}
        />
      </Flex>

      {reasons.length ? (
        <Flex direction="column" gap="6px" mt="12px" p="10px" borderRadius="8px" bg={M.field}>
          <Text fontSize="12px" fontWeight={500} color={M.tertiary}>
            Vì sao chưa được chi trả?
          </Text>
          {reasons.map((reason) => (
            <Flex key={reason.code} gap="8px" align="flex-start">
              <Box w="5px" h="5px" borderRadius="3px" mt="7px" bg={ui.color} flexShrink={0} />
              <Text fontSize="14px" fontWeight={500} color={M.baseContent} flex="1">
                {reason.text}
              </Text>
            </Flex>
          ))}
        </Flex>
      ) : null}
    </Box>
  );
};

/* ------------------------------------------ khối "chưa chi trả theo dự án" */
export const UnpaidByProjectPanel = ({ items = [] }) => {
  if (!items.length) return null;
  return (
    <Box
      mt="16px"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
      bg="white"
    >
      <Text fontSize="12px" fontWeight={500} color={M.tertiary}>
        CHƯA CHI TRẢ THEO DỰ ÁN
      </Text>
      <Flex direction="column" gap="10px" mt="10px">
        {items.map((project) => (
          <Flex key={project.projectId || project.projectCode} align="flex-start" gap="12px">
            <Text fontSize="14px" fontWeight={500} color={M.baseContent} flex="0.58" noOfLines={2}>
              {`${project.projectName || "-"} (${project.totalTransaction} GD)`}
            </Text>
            <Text
              fontSize="14px"
              fontWeight={600}
              color={M.primary}
              flex="0.42"
              textAlign="right"
            >
              {formatVnd(project.totalUnpaid || 0)}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

/* --------------------------------------------- bảng giao dịch (desktop) */
export const unpaidLockingRows = (items = []) =>
  items.map((it, i) => ({
    id: it?._id || i,
    raw: it,
    title: unpaidTitleOf(it) || "-",
    date: unpaidDateOf(it),
    customer: unpaidCustomerOf(it),
    myFee: formatVnd(it?.myRemainingRevenueAfterTax || 0),
    paid: formatVnd(it?.myPaidRemainingRevenueAfterTax || 0),
    unpaid: formatVnd(it?.myUnpaidRemainingRevenueAfterTax || 0),
    ui: unpaidStatusUi(it?.paymentStatus),
  }));

export const UNPAID_LOCKING_HEADERS = [
  { key: "title", label: "Dự án / Căn hộ", align: "left", width: "22%" },
  { key: "date", label: "Thời gian", width: "11%" },
  { key: "customer", label: "Khách hàng", align: "left", width: "15%" },
  { key: "myFee", label: "PDV của tôi", align: "right", width: "14%" },
  { key: "paid", label: "Đã được chi trả", align: "right", width: "14%" },
  {
    key: "unpaid",
    label: "Chưa được chi trả",
    align: "right",
    width: "14%",
    render: (row) => (
      <Text fontSize="14px" fontWeight={600} color={M.primary}>
        {row.unpaid}
      </Text>
    ),
  },
  {
    key: "status",
    label: "Trạng thái",
    width: "13%",
    render: (row) => (
      <Box display="inline-block" bg={row.ui.bg} borderRadius="6px" px="10px" py="3px">
        <Text fontSize="12px" fontWeight={600} color={row.ui.color}>
          {row.ui.label}
        </Text>
      </Box>
    ),
  },
];
